import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../api";
import { Lock, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [ready, setReady] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const readyRef = useRef(false);

  useEffect(() => {
    // The reset link puts a one-time recovery token in the URL; Supabase's
    // client picks it up automatically and fires this event once it's
    // established a temporary session scoped only to changing the password.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        readyRef.current = true;
        setReady(true);
      }
    });

    // If the page was already open when the event fired (fast networks),
    // also check directly for an existing recovery session.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        readyRef.current = true;
        setReady(true);
      }
    });

    const timeout = setTimeout(() => {
      if (!readyRef.current) setInvalidLink(true);
    }, 4000);

    return () => { sub.subscription.unsubscribe(); clearTimeout(timeout); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) {
      setError(updateError.message || "Failed to update password. The link may have expired.");
      return;
    }
    await supabase.auth.signOut();
    setDone(true);
    setTimeout(() => navigate("/login", { replace: true }), 2500);
  };

  return (
    <div style={{ backgroundColor: "#060D1A", minHeight: "100vh" }}
      className="flex items-center justify-center p-4 relative overflow-hidden">

      <div style={{
        position: "absolute", top: "-20%", left: "50%", transform: "translateX(-50%)",
        width: "600px", height: "600px",
        background: "radial-gradient(circle, rgba(0,229,204,0.07) 0%, transparent 65%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(0,229,204,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,204,0.025) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      <div className="w-full max-w-sm relative">
        <div style={{
          backgroundColor: "#0D1F3C",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,229,204,0.05)",
        }} className="rounded-3xl p-8">

          <div className="text-center mb-8">
            <img src="/logo192.jpg" alt="FBL Academy"
              style={{ boxShadow: "0 0 24px rgba(0,229,204,0.35)" }}
              className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4" />
            <h1 className="text-xl font-extrabold text-white tracking-tight">Set a new password</h1>
          </div>

          {done ? (
            <div className="text-center">
              <div style={{ backgroundColor: "rgba(0,229,204,0.1)", color: "#00E5CC" }}
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={22} />
              </div>
              <p className="text-gray-400 text-sm">Password updated. Redirecting to sign in…</p>
            </div>
          ) : ready ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div style={{ backgroundColor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}
                  className="flex items-center gap-2 rounded-xl p-3">
                  <AlertCircle size={15} className="text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div>
                <label className="text-xs text-gray-400 mb-1.5 block font-medium">New password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    style={{ backgroundColor: "#080F1E", border: "1px solid rgba(255,255,255,0.08)", color: "white" }}
                    className="w-full rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                    type="password" placeholder="At least 8 characters"
                    value={password} onChange={e => setPassword(e.target.value)} required />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 mb-1.5 block font-medium">Confirm password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    style={{ backgroundColor: "#080F1E", border: "1px solid rgba(255,255,255,0.08)", color: "white" }}
                    className="w-full rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/40 transition-all"
                    type="password" placeholder="••••••••"
                    value={confirm} onChange={e => setConfirm(e.target.value)} required />
                </div>
              </div>

              <button
                style={loading
                  ? { backgroundColor: "rgba(0,229,204,0.4)", color: "#080F1E" }
                  : { background: "linear-gradient(135deg, #00E5CC 0%, #00BFA5 100%)", color: "#080F1E", boxShadow: "0 4px 16px rgba(0,229,204,0.3)" }}
                className="w-full py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all flex items-center justify-center gap-2 mt-2"
                type="submit" disabled={loading}>
                {loading ? "Updating…" : <><span>Update Password</span><ArrowRight size={15} /></>}
              </button>
            </form>
          ) : invalidLink ? (
            <div className="text-center">
              <div style={{ backgroundColor: "rgba(239,68,68,0.1)", color: "#F87171" }}
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={22} />
              </div>
              <p className="text-gray-400 text-sm mb-6">
                This reset link is invalid or has expired. Request a new one from the sign-in page.
              </p>
              <Link to="/forgot-password" className="text-sm font-semibold" style={{ color: "#00E5CC" }}>
                Request a new link
              </Link>
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center py-4">Verifying your link…</p>
          )}
        </div>
      </div>
    </div>
  );
}
